import React, { forwardRef, useRef } from 'react'
import { TextInput } from 'react-native'
import { DS } from '@/design-system'
import DatePickerWeb from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

interface MTTDateTimePickersProps {
  value: Date
  onChange: (date: Date) => void
}

const POPPER_CLASS = 'mtt-datepicker-popper'

// biome-ignore lint/suspicious/noExplicitAny: react-datepicker input props
const CustomInput = forwardRef((props: any, ref) => (
  <TextInput
    {...props}
    ref={ref}
    editable={false}
    style={{
      // Same box as the create-game ScheduleDateTimeFields web input.
      height: 48,
      borderWidth: 1,
      borderColor: DS.palette['Brand/Light Grey'],
      borderRadius: 8,
      paddingHorizontal: 12,
      fontSize: 16,
      color: DS.palette['Global/Black'],
      backgroundColor: DS.palette['Global/White'],
      width: '100%'
    }}
  />
))

function isSameLocalDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function endOfLocalDay(d: Date) {
  const eod = new Date(d)
  eod.setHours(23, 59, 0, 0)
  return eod
}

export function MTTDateTimePickers({ value, onChange }: MTTDateTimePickersProps) {
  const dateInputRef = useRef(null)
  const timeInputRef = useRef(null)

  // When the selected date is today, clamp time picker to "now or later" so the
  // user can't pick an already-past time. The server validates this too, but
  // UI clamping prevents the silent rejection on submit.
  const now = new Date()
  const isToday = isSameLocalDay(value, now)
  const minTime = isToday ? now : undefined
  const maxTime = isToday ? endOfLocalDay(value) : undefined

  return (
    <>
      <style>{`
        .${POPPER_CLASS} {
          z-index: 10000 !important;
        }
      `}</style>
      <DS.Frame row gap="20">
        <DS.Frame gap="6" style={{ flex: 1 }}>
          <DS.Text variant="Body Medium/Regular" style={{ color: DS.palette['Global/Black'] }}>
            Date
          </DS.Text>
          <DatePickerWeb
            selected={value}
            onChange={(d) => {
              if (d) {
                const merged = new Date(value)
                merged.setFullYear(d.getFullYear(), d.getMonth(), d.getDate())
                // If the new date is today and the existing time is already past,
                // bump to "now" so the value passed back is always valid.
                const nowAfterDateChange = new Date()
                if (isSameLocalDay(merged, nowAfterDateChange) && merged.getTime() < nowAfterDateChange.getTime()) {
                  merged.setHours(nowAfterDateChange.getHours(), nowAfterDateChange.getMinutes())
                }
                onChange(merged)
              }
            }}
            dateFormat="EEE MMM d"
            minDate={new Date()}
            customInput={<CustomInput ref={dateInputRef} />}
            popperClassName={POPPER_CLASS}
          />
        </DS.Frame>
        <DS.Frame gap="6" style={{ flex: 1 }}>
          <DS.Text variant="Body Medium/Regular" style={{ color: DS.palette['Global/Black'] }}>
            Time
          </DS.Text>
          <DatePickerWeb
            selected={value}
            onChange={(d) => {
              if (d) onChange(d)
            }}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={5}
            timeCaption="Time"
            dateFormat="h:mm aa"
            minTime={minTime}
            maxTime={maxTime}
            customInput={<CustomInput ref={timeInputRef} />}
            popperClassName={POPPER_CLASS}
          />
        </DS.Frame>
      </DS.Frame>
    </>
  )
}
